import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

/**
 * A wrapper component for routes that require authentication
 * 
 * @param children - The components to render if authenticated
 * @param requireOnboarding - If true, redirects to onboarding if user hasn't completed it
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireOnboarding = false 
}) => {
  const { isAuthenticated, isLoading, profile, user } = useAuth();
  const location = useLocation();
  const { isDark } = useTheme();

  // Clear any problematic URL parameters that might cause redirect loops
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasProblematicParams = searchParams.has('p') || 
                                  searchParams.has('error_code') || 
                                  searchParams.has('error_description');
    
    if (hasProblematicParams && isAuthenticated) {
      // Clear problematic parameters for authenticated users on protected routes
      const newUrl = location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location, isAuthenticated]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-100' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isDark ? 'border-neon-cyan' : 'border-blue-500'} mx-auto mb-4`}></div>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Verifying authentication...
          </p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login with current location
  if (!isAuthenticated || !user) {
    const redirectTo = location.pathname !== '/login' ? location.pathname + location.search : '/dashboard';
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTo)}`} replace />;
  }

  // If onboarding is required but not completed, redirect to onboarding
  if (requireOnboarding && profile && !profile.has_completed_onboarding) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  // If authenticated (and onboarding completed if required), render children
  return <>{children}</>;
};

export default ProtectedRoute;
